// Module-level mode segments that were RENAMED, and the two places a rename
// has to keep working after it ships: a URL someone saved, and a mode someone
// stored.
//
// Aaron, 2026-09-28 (H94): "there should be no 'overviews' at a module level -
// rename these to dashboard same as .../app/job_costs/dashboard". A module's
// KPI mode is `dashboard`, labelled "Dashboard" — the Financial Reports shape
// (src/financial-reports/index.tsx). src/blank/PATTERNS.md "Module weight" is
// the rule; src/__tests__/noModuleOverviewMode.test.ts keeps "overview" from
// coming back as a mode.
//
// Renaming a segment breaks two things nobody sees in a code review:
//
// 1. SAVED URLS. `/app/<module>/overview` is in bookmarks, emails, Hub pages
//    and browser history. Every renamed module keeps a route on the OLD
//    segment that renders <RenamedModeRedirect to="dashboard" />.
// 2. STORED MODES. `<module>.viewMode` in localStorage still says "overview"
//    for everyone who last used that mode. A layout validating the stored
//    value against its new MODE_LABELS would silently reset those users to the
//    default mode. Each `readRememberedModeSegment` runs the stored value
//    through `migrateModeSegment` first; `useViewMode` does the same.
import { Navigate, useLocation } from "react-router-dom";

/** Old segment -> the segment that replaced it. */
export const RENAMED_MODE_SEGMENTS: Readonly<{ [oldSegment: string]: string }> =
  {
    overview: "dashboard",
  };

/**
 * The current name for a mode segment: its replacement when it was renamed,
 * otherwise itself. hasOwnProperty, NOT `in` — a stored "constructor" must not
 * resolve to Object's constructor (f74a205b / 2deb1dee).
 */
export function migrateModeSegment(segment: string): string;
export function migrateModeSegment(segment: string | null): string | null;
export function migrateModeSegment(segment: string | null): string | null {
  if (segment === null) return null;
  return Object.prototype.hasOwnProperty.call(RENAMED_MODE_SEGMENTS, segment)
    ? RENAMED_MODE_SEGMENTS[segment]
    : segment;
}

/**
 * Route element for an OLD mode segment: swaps the last path segment for `to`
 * and keeps the query string and hash, so `/user_management/overview?x=1`
 * lands on `/user_management/dashboard?x=1`.
 *
 * Built as an ABSOLUTE path from the live pathname, not a relative `..`:
 * relative navigation resolves against the ROUTE hierarchy, and the mode
 * routes sit under a PATHLESS layout route, so how many `..` a hop needs is
 * not readable from the JSX (see ModeSegmentRedirect in
 * src/companies/CompanyList.tsx). The module base is nav-driven and is never
 * hardcoded.
 */
export const RenamedModeRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { pathname, search, hash } = useLocation();
  const segments = pathname.split("/").filter(Boolean);
  const base = segments.slice(0, -1);
  return <Navigate to={`/${[...base, to].join("/")}${search}${hash}`} replace />;
};
